import Matrix, {MatrixError} from "util/table/Matrix";

// A plain decimal number: an optional sign, digits with an optional point (or a leading point), and an optional exponent. Nothing else is a number
// to this check, whatever parseFloat makes of it.
const PLAIN_NUMBER = /^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/;

/**
 * Reads text pasted from a spreadsheet (tab between the cells, a line end between the rows) into a matrix of the numbers it holds, or refuses it.
 *
 * Matrix.fromExcelString reads each cell with parseFloat, which is too kind for a tuning map: it takes "12abc" for 12 and "1,234.5" for 1, "0x10" for
 * 0 and "Infinity" for Infinity (which JSON turns into null on the way out), and it throws a MatrixError, which nothing caught, for a cell that is not
 * a number at all or for rows of different lengths. A wrong number written into a map is worse than a refused paste, so the text is checked first: the
 * one line end that an Excel copy ends with is trimmed, and then every cell, trimmed, must be a plain decimal number. An empty cell, in the block or at
 * its edge, is not one. A number so large that it reads as Infinity is refused too. Nothing is sent or changed on a refusal; that is the caller's.
 * @param {string} text - what the clipboard holds
 * @param {number} [precision] - the precision of the matrix the cells are going into
 * @returns {{ok: true, matrix: Matrix}|{ok: false}}
 */
export const parsePaste = (text, precision) => {
    const block = text.replace(/\r?\n$/, "");
    const cellsArePlain = block.split("\n").every(row => row.split("\t").every(cell => PLAIN_NUMBER.test(cell.trim())));
    if (!cellsArePlain) return {ok: false};

    let matrix;

    try {
        matrix = Matrix.fromExcelString(block, precision);
    } catch (error) {
        if (error instanceof MatrixError) return {ok: false};
        throw error;
    }

    return matrix.every(Number.isFinite) ? {ok: true, matrix} : {ok: false};
};
